// Servicio de auth de Web Bochas.
// Maneja login, signup del primer admin, y estado de sesión.

import { recordRequestError } from "@/services/http/requestDiagnostics";

const API_BASE = process.env.VUE_APP_BASEURL || "/api";

const TOKEN_KEY = "bochas_token";
const USER_KEY = "bochas_user";

async function request(url, options) {
  let response;
  try {
    response = await fetch(url, options);
  } catch (_) {
    recordRequestError({ url, method: options?.method });
    throw new Error("No pudimos conectarnos. Intentá nuevamente en unos segundos.");
  }
  if (!response.ok) recordRequestError({ url, method: options?.method, status: response.status, code: "HTTP_ERROR" });
  if (response.status >= 500) {
    throw new Error("El servicio no está disponible por el momento. Intentá nuevamente en unos segundos.");
  }
  return response;
}

// ============ Storage ============
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(`${TOKEN_KEY}_refresh`);
}

export function getStoredUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveSession({ accessToken, refreshToken, user }) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(`${TOKEN_KEY}_refresh`, refreshToken);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

let refreshPromise = null;

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(`${TOKEN_KEY}_refresh`);
  localStorage.removeItem(USER_KEY);
}

// ============ API calls ============

/**
 * Login con email + password.
 * Retorna { user } y guarda el token en localStorage.
 */
export async function signIn(email, password) {
  const res = await request(`${API_BASE}/auth/signin-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || "Error al iniciar sesión.");
  }
  saveSession(data);
  return data;
}

/**
 * Crea el primer usuario admin (solo funciona si no existe ninguno).
 */
export async function setupAdmin({ email, password, firstname, lastname }) {
  const res = await request(`${API_BASE}/auth/setup-admin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, firstname, lastname }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || "Error al crear el administrador.");
  }
  saveSession(data);
  return data;
}

export async function getSetupStatus() {
  const res = await request(`${API_BASE}/auth/setup-status`);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || "Error al verificar la configuración.");
  }
  return data;
}

/**
 * Devuelve el usuario autenticado (valida token contra el backend).
 */
export async function getMe() {
  const token = getToken();
  if (!token) return null;
  try {
    let res = await request(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401 && (await refreshSession())) {
      res = await request(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
    }
    if (!res.ok) {
      clearSession();
      return null;
    }
    const data = await res.json();
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    return data.user;
  } catch {
    return getStoredUser();
  }
}

export function logout() {
  clearSession();
}

export async function refreshSession() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  if (!refreshPromise) {
    refreshPromise = request(`${API_BASE}/auth/refresh-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = await res.json();
        saveSession(data);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// ============ Admin: gestión de usuarios ============

/**
 * Lista todos los usuarios con role BOCHAS_ADMIN. Solo para admins.
 */
export async function listarUsuarios() {
  const res = await authFetch("/auth/users");
  if (!res.ok) {
    throw new Error("Error al listar usuarios.");
  }
  return res.json();
}

/**
 * Crea un nuevo usuario admin con password temporal.
 * El usuario se crea con mustChangePassword: true.
 */
export async function crearUsuario({
  email,
  password,
  firstname,
  lastname,
  role,
  isDeveloper,
  asociacionId,
  clubId,
}) {
  const res = await authFetch("/auth/users", {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      firstname,
      lastname,
      role,
      isDeveloper,
      asociacionId,
      clubId,
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || "Error al crear el usuario.");
  }
  return data;
}

/**
 * Cambia la contraseña de un usuario.
 * Si `isAdminReset = true`, indica que es un admin reseteando la contraseña de otro
 * user; el backend no pide `currentPassword` y marca `mustChangePassword = true`
 * en el target.
 */
export async function cambiarPassword({ userId, currentPassword, newPassword, isAdminReset = false }) {
  const res = await authFetch(`/auth/users/${userId}/password`, {
    method: "PATCH",
    body: JSON.stringify({ currentPassword, newPassword, isAdminReset }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || "Error al cambiar la contraseña.");
  }
  return data;
}

/**
 * Edita la información de un usuario (firstname, lastname, email).
 * Solo admins pueden hacerlo.
 */
export async function editarUsuario({
  userId,
  firstname,
  lastname,
  email,
  role,
  isDeveloper,
  asociacionId,
  clubId,
}) {
  const res = await authFetch(`/auth/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({
      firstname,
      lastname,
      email,
      role,
      isDeveloper,
      asociacionId,
      clubId,
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || "Error al editar el usuario.");
  }
  return data;
}

/**
 * Elimina un usuario (solo admins).
 * El backend valida que no se pueda eliminar a sí mismo.
 */
export async function eliminarUsuario(userId) {
  const res = await authFetch(`/auth/users/${userId}`, {
    method: "DELETE",
  });
  if (res.status === 204 || res.status === 200) {
    return res.json().catch(() => ({ ok: true }));
  }
  const data = await res.json().catch(() => ({}));
  throw new Error(data.message || "Error al eliminar el usuario.");
}

/**
 * Helper para hacer requests autenticados.
 */
export async function authFetch(url, options = {}) {
  const makeRequest = () => {
    const token = getToken();
    const headers = {
      "Content-Type": "application/json",
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    return request(`${API_BASE}${url}`, { ...options, headers });
  };

  let res = await makeRequest();
  if (res.status === 401) {
    if (await refreshSession()) {
      res = await makeRequest();
    } else {
      clearSession();
    }
  }
  return res;
}

/**
 * Helper para detectar si un 403 viene de "debe cambiar contraseña".
 * Devuelve un objeto con la info del error; lanza Error si es otro 403.
 */
export async function authFetchJson(url, options = {}) {
  const res = await authFetch(url, options);
  if (res.status === 403) {
    const data = await res.json().catch(() => ({}));
    if (data.mustChangePassword) {
      return { mustChangePassword: true, message: data.message };
    }
    throw new Error(data.message || "Acceso denegado.");
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Error ${res.status}.`);
  }
  return res.json();
}
